import Vue from 'vue'

// ========== 常量定义 ==========
const PERMISSION_CACHE_KEY = 'permission_cache'
const PERMISSION_CONFIG = {
  imgSource: {
    name: '相机/相册',
    title: '"EMS中国邮件速递物流"申请使用相机/相册权限',
    purpose: '为您提供身份证拍照上传、客服对话拍照上传、图片地址识别、托寄物照片上传、邮件报关材料上传、投诉理赔与客服对话图片上传等服务。'
  },
  camera: {
    name: '相机',
    title: '"EMS中国邮件速递物流"申请使用相机权限',
    purpose: '为您提供扫码寄件、扫码查单、扫码支付、身份证拍照上传、客服对话拍照上传等服务。',
    settingGuide: '相机功能已受限，请在手机系统设置中启用'
  },
  album: {
    name: '相册',
    title: '"EMS中国邮件速递物流"申请使用相册权限',
    purpose: '为您提供图片识别、托寄物照片上传、邮件报关相关材料图片上传、投诉理赔与客服对话图片上传等服务。'
  },
  microphone: {
    name: '麦克风',
    title: '"EMS中国邮件速递物流"申请使用麦克风权限',
    purpose: '为您提供运单、地址信息语音录入等服务。'
  },
  location: {
    name: '定位',
    title: '"EMS中国邮件速递物流"申请使用定位权限',
    purpose: '为您提供附近网点、当前寄件位置等查询服务。'
  },
  bluetooth: {
    name: '蓝牙',
    title: '"EMS中国邮件速递物流"申请使用蓝牙权限',
    purpose: '为您提供蓝牙打印面单服务。'
  },
  phone: {
    name: '电话',
    title: '"EMS中国邮件速递物流"申请使用电话权限',
    purpose: '为您提供电话联系快递员、客服、网点等服务。'
  },
  clipboard: {
    name: '剪贴板',
    title: '"EMS中国邮件速递物流"申请使用剪贴板权限',
    purpose: '为您提供寄件名址、快递单号复制等服务。'
  },
  storage: {
    name: '存储',
    title: '"EMS中国邮件速递物流"申请使用存储权限',
    purpose: '为您提供图片、票据、文件保存或上传等服务'
  }
}

// ========== Vue 事件总线 ==========
export const eventBus = new Vue()

// ========== 全局弹窗实例 ==========
let permissionDialogInstance = null

/**
 * 初始化全局弹窗
 */
export const initPermissionDialog = () => {
  if (typeof window === 'undefined' || permissionDialogInstance) return

  const PermissionDialogComponent = Vue.extend(require('@/components/messageBox/index.vue').default)
  permissionDialogInstance = new PermissionDialogComponent()
  permissionDialogInstance.$mount()
  document.body.appendChild(permissionDialogInstance.$el)
}

/**
 * 获取全局弹窗实例
 */
export const getPermissionDialog = () => {
  return permissionDialogInstance
}

// ========== 缓存管理 ==========

/**
 * 获取所有权限缓存数据
 */
const getPermissionCache = () => {
  try {
    const cacheJson = window.localStorage.getItem(PERMISSION_CACHE_KEY)
    if (cacheJson) {
      return JSON.parse(cacheJson)
    }
    return {}
  } catch (error) {
    console.warn('获取权限缓存失败，返回空对象', error)
    return {}
  }
}

/**
 * 设置指定权限的缓存状态
 */
const setPermissionCache = (type, granted) => {
  console.log('setPermissionCache')
  try {
    const cache = getPermissionCache()
    cache[type] = granted
    window.localStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify(cache))
  } catch (error) {
    console.error('设置权限缓存失败:', error)
  }
}

/**
 * 检查缓存中指定权限的授权状态
 */
export const checkPermissionCache = (type) => {
  const cache = getPermissionCache()
  return !!cache[type]
}

/**
 * 清除权限缓存
 */
export const clearPermissionCache = (type) => {
  try {
    if (type) {
      const cache = getPermissionCache()
      delete cache[type]
      window.localStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify(cache))
    } else {
      window.localStorage.removeItem(PERMISSION_CACHE_KEY)
    }
  } catch (error) {
    console.error('清除权限缓存失败:', error)
  }
}

// ========== 核心权限管理 ==========

/**
 * 显示权限用途说明弹窗 (使用全局组件)
 * @param {string} type 权限类型
 * @returns {Promise<boolean>} 用户是否确认
 */
const showPermissionDialog = (type) => {
  if (typeof window === 'undefined') {
    console.warn('showPermissionDialog: 不在浏览器环境中，无法显示弹窗。')
    return Promise.resolve(false)
  }

  const config = PERMISSION_CONFIG[type]
  if (!config) {
    console.warn(`未找到权限类型: ${type} 的配置`)
    return Promise.resolve(false)
  }

  // 使用全局弹窗组件
  if (permissionDialogInstance) {
    return permissionDialogInstance.showMsgBox({
      title: config.title,
      message: config.purpose,
      showCancelButton: true,
      closeOnClickModal: false,
      confirmButtonText: '允许',
      cancelButtonText: '不允许',
      contentClasses: 'flex-center'
    }).then(res => {
      return res === 'confirm'
    })
  } else {
    console.warn('权限弹窗组件未初始化，请调用 initPermissionDialog()')
    return Promise.resolve(false)
  }
}

/**
 * 检查并请求权限（Vue2 自定义组件版）
 * @param {string} type 权限类型
 * @returns {Promise<{granted: boolean, canUse: boolean}>} 权限结果
 */
export const checkAndRequestPermissionH5 = async (type) => {
  if (typeof window === 'undefined') {
    return { granted: false, canUse: false }
  }

  const isCachedAllowed = checkPermissionCache(type)

  if (isCachedAllowed) {
    return { granted: true, canUse: true }
  }

  const userConfirmed = await showPermissionDialog(type)

  if (userConfirmed) {
    setPermissionCache(type, true)
    return { granted: true, canUse: true }
  } else {
    return { granted: false, canUse: false }
  }
}

/**
 * 手动设置权限状态（直接写入缓存）
 * @param {string} type 权限类型
 * @param {boolean} granted 是否授权
 * @returns {boolean} 设置操作是否成功
 */
export const setPermissionStatus = (type, granted) => {
  if (typeof window === 'undefined') {
    console.warn('setPermissionStatus: 不在浏览器环境中，无法设置缓存。')
    return false
  }

  try {
    setPermissionCache(type, granted)
    console.log(`权限[${type}]已手动设置为: ${granted ? '允许' : '拒绝'}`)
    return true
  } catch (error) {
    console.error(`手动设置权限[${type}]状态失败:`, error)
    return false
  }
}

/**
 * 检查权限状态（仅缓存）
 * @param {string} type 权限类型
 * @returns {{granted: boolean, canUse: boolean}} 权限状态对象
 */
export const checkPermissionStatus = (type) => {
  if (typeof window === 'undefined') {
    console.warn('checkPermissionStatus: 不在浏览器环境中，默认返回未授权。')
    return { granted: false, canUse: false }
  }

  const isCachedAllowed = checkPermissionCache(type)
  return { granted: isCachedAllowed, canUse: isCachedAllowed }
}

// 导出权限配置
export { PERMISSION_CONFIG, setPermissionCache }
